import { From } from '@/data/constant'
import { PartType } from '@/partTypes'
import store from '@/store'
import type { AwaitStoreItem } from '@/store/modules/awaitPaibanStore'
import type { BujianStoreItem } from '@/store/modules/bujianStore'
import {
  autoRollOver,
  dealPlankExpandKnife,
  getSawKnife,
  isSawPaiban,
  isWeldingPart,
  reversalPlank,
  rolloverPlank,
} from '@/util/LayoutFuncs'
import { addPropForPart, dealExpandSlot, rotatePart } from '@/util/LayoutTool'
import {
  checkPlankHoleSlot,
  dealPlankPoly,
  getPlateKnifeDiameter,
} from '@/util/dealPaibanData'
import { LineSlotType, dealPlankHoleSlot } from '@/util/dealPlankDataFuncs'
import {
  clearTextureGrainInfo,
  isTextureAlignmentPlank,
} from '@/util/plankAlignTexture'
import { getPlankMatCode, getPlankNextIndex } from '@/util/plankCommonFuncs'
import { initPart } from '@/views/materialsList/utils'
import { setCachedRawPlank } from '@/views/newPaiban/util/renderUrlCache'
import { message } from 'ant-design-vue'
import { Ref, ref } from 'vue'

import { getPlankImage } from '.'

type StoreDragItem = AwaitStoreItem | BujianStoreItem

interface DragTargetData {
  el: HTMLElement
  width: number
  height: number
  offsetX: number
  offsetY: number
}

interface DragClickOptions {
  resetImageUrl?: (url: string) => Promise<void>
}

interface IImageOptions {
  defaultScale: number
  scale: number
  plankLineWidth: number
}

interface IUseStoreDragPlankOptions<T extends StoreDragItem> {
  getImageOptions: () => IImageOptions
  loadErrorText: string
  currentDragMutation?: {
    setMutation: string
    updateMutation: string
  }
  getDragPlank: (currentDrag: T) => Promise<PartType | null | undefined>
  addStoreFeature: (plank: PartType, currentDrag: T) => void
  shouldLoadDragPlank?: (currentDrag: T) => boolean
}

interface IUseStoreDragPlankResult<T extends StoreDragItem> {
  currentDragTarget: Ref<DragTargetData | null>
  handleDragClick: (
    dragTargetData: DragTargetData,
    currentDrag: T,
    options?: DragClickOptions
  ) => Promise<void>
  handleTargetPositionChange: (data: DragTargetData, type: 'reset') => void
  getCurrentDragTarget: () => DragTargetData | null
}

export function useStoreDragPlank<T extends StoreDragItem>(
  options: IUseStoreDragPlankOptions<T>
): IUseStoreDragPlankResult<T> {
  const currentDragTarget = ref<DragTargetData | null>(null)
  const currentDragMutation = options.currentDragMutation ?? {
    setMutation: 'awaitPaibanStore/setCurrentDragPlank',
    updateMutation: 'awaitPaibanStore/updateCurrentDragPlank',
  }

  async function handleDragClick(
    dragTargetData: DragTargetData,
    currentDrag: T,
    dragOptions?: DragClickOptions
  ) {
    store.commit(currentDragMutation.setMutation, currentDrag)
    currentDragTarget.value = dragTargetData
    store.commit('changeDragingPlank', true)
    await loadDragPlank(currentDrag, dragTargetData)
    await reloadPlankImage(currentDrag, dragOptions?.resetImageUrl)
  }

  async function loadDragPlank(currentDrag: T, dragTargetData: DragTargetData) {
    const shouldLoad = options.shouldLoadDragPlank
      ? options.shouldLoadDragPlank(currentDrag)
      : currentDrag.type === 'serve' &&
        (currentDrag.isDone !== 'done' || !currentDrag.plank)
    if (!shouldLoad) return currentDrag.plank
    try {
      const plank = await options.getDragPlank(currentDrag)
      if (!plank) {
        throw new Error('Plank is empty')
      }
      const { el: image, width, height } = dragTargetData
      image.style.width = `${width}px`
      image.style.height = `${height}px`
      image.style.transition = 'width 0.3s ease, height 0.3s ease'

      if (currentDrag.partUniqueId) {
        plank.partUniqueId = currentDrag.partUniqueId
      }
      const renderUrl = (currentDrag as Record<string, unknown>).render_url as
        | string
        | undefined
      if (renderUrl) {
        setCachedRawPlank(plank.partUniqueId, plank)
      }
      options.addStoreFeature(plank, currentDrag)
      plank.index = getPlankNextIndex()
      plank._isNewTableBujian = getStoreDragItemNewStatus(currentDrag)
      initData(plank)
      await dealPlankHoleSlot([plank], store.state.ncSetting)
      dealDragPlank(plank, currentDrag)
      currentDrag.isDone = 'done'
      currentDrag.plank = plank
      store.commit(currentDragMutation.updateMutation, plank)
      return plank
    } catch (error) {
      currentDrag.isDone = 'err'
      message.error(options.loadErrorText)
      console.error(error)
      return null
    }
  }

  async function reloadPlankImage(
    currentDrag: T,
    resetImageUrl?: (url: string) => Promise<void>
  ) {
    if (!currentDrag.plank || !resetImageUrl) return
    const plankBase64 = getPlankImage(
      currentDrag.plank,
      true,
      options.getImageOptions()
    )
    if (!plankBase64) return
    await resetImageUrl(plankBase64)
  }

  function handleTargetPositionChange(data: DragTargetData, type: 'reset') {
    currentDragTarget.value = data
    store.commit('setOffsetPointbetweenCacheImage', {
      x: data.offsetX,
      y: data.offsetY,
    })
    if (type === 'reset') {
      data.el.style.width = `${data.width}px`
      data.el.style.height = `${data.height}px`
    }
  }

  function getCurrentDragTarget() {
    return currentDragTarget.value
  }

  return {
    currentDragTarget,
    handleDragClick,
    handleTargetPositionChange,
    getCurrentDragTarget,
  }
}

function dealDragPlank(plank: PartType, originData?: StoreDragItem) {
  checkImportantFields(plank, originData)
  const knife = {
    knife: {
      diameter: getPlateKnifeDiameter(plank.stockKey, store.state.ncSetting),
    },
  }
  const ncSetting = { ...store.state.ncSetting, ...knife }
  addPropForPart(plank, {}, ncSetting)
  dealPlankRoll(plank, ncSetting)
  const parts = store.state.paibanData.map((item) => item.parts).flat(1)
  dealPlankIsUseSawForBujian(plank)
  dealPlankExpandKnife(
    plank,
    ncSetting,
    !Object.prototype.hasOwnProperty.call(plank, 'path') &&
      parts.every((part) => part.isUseSaw)
  )
  dealPlankPoly(plank)
  checkPlankHoleSlot(plank)
  const stockKey = `${plank.texture}:${plank.matCode}:${plank.thick}`
  const cutD = plank.isUseSaw
    ? getSawKnife(ncSetting)
    : getPlateKnifeDiameter(stockKey, ncSetting)
  const expandMap = dealExpandSlot(plank, ncSetting, Number(cutD) / 2)
  if (expandMap && Object.values(expandMap).some((item) => Boolean(item))) {
    plank.isExpanded = true
  }
  if (ncSetting.xyReverse) {
    rotatePart(plank, 'storeDrag-xyReverse')
  }
  // 拖动过来的板件不可存在对纹信息
  if (isTextureAlignmentPlank(plank)) {
    clearTextureGrainInfo(plank)
  }
}


function dealPlankIsUseSawForBujian(part: PartType) {
  const ncSetting = store.state.ncSetting
  const sawKnives = ncSetting.sawSetting?.mentaoji_saws
  const sawCutOnlyWeld =
    ncSetting.sawSetting.sawCutOnlyWeld &&
    ncSetting.sawSetting.processType === 'weldingPlank'
  const sawToLayout =
    ncSetting.sawSetting.mentaoji_enable &&
    ncSetting.sawSetting.sawToLayout &&
    ncSetting.sawSetting.processType === 'weldingPlank' &&
    isSawPaiban(store)
  // 门套切割 下料刀半径（不能）>锯片厚度 且不支持异形排版
  const isDoorFrame =
    ncSetting.sawSetting.mentaoji_enable &&
    ncSetting.sawSetting.processType === 'doorFrame' &&
    isSawPaiban(store)
  if (
    (sawToLayout || isDoorFrame) &&
    ((sawKnives &&
      sawKnives.some(
        (item: any) => item.saw_direction == 'H' && item.saw_width
      )) ||
      ncSetting.sawSetting?.horizontalWidth)
  ) {
    if (sawCutOnlyWeld) {
      part.isUseSaw = true
      // 锯切排版只排焊接板 这里其他材质小板，就不能使用电子锯了
      if (!isWeldingPart(part)) {
        part.isUseSaw = false
      }
    } else {
      part.isUseSaw = true
    }
  }
}

function initData(plank: PartType) {
  initPart(plank as any)
  const { slots = [], lineSlots = [] } = plank
  const slotList = window.$copy(slots)
  const lineSlotList = window.$copy(lineSlots)
  slotList.forEach((slot: PartType['slots'][number]) => {
    slot.oriPt1 = slot.pt1 ? window.$copy(slot.pt1) : undefined
    slot.oriPt2 = slot.pt2 ? window.$copy(slot.pt2) : undefined
  })
  lineSlotList.forEach((slot: LineSlotType) => {
    slot.oriPath = slot.path ? window.$copy(slot.path) : undefined
  })
}

function dealPlankRoll(plank: PartType, ncSetting: any) {
  const { is_high_gloss_plank, high_gloss_side } = plank
  const { highgloss_direction, high_gloss_plank_no_rotate } = ncSetting
  if (is_high_gloss_plank && high_gloss_side !== 0) {
    const isRollFront = highgloss_direction === 'front' && high_gloss_side === 1
    const isRollBack = highgloss_direction === 'back' && high_gloss_side === -1
    if (isRollFront || isRollBack) {
      if (high_gloss_plank_no_rotate) {
        reversalPlank(plank)
      } else {
        rolloverPlank(plank)
      }
    }
  } else {
    autoRollOver(plank, true)
    if (ncSetting.xyReverse) {
      rolloverPlank(plank)
    }
  }
}

function checkImportantFields(plank: PartType, originData?: StoreDragItem) {
  plank.startX = plank.startX ?? 0
  plank.startY = plank.startY ?? 0
  plank.matCode = getPlankMatCode(plank)
  plank.stockKey = `${plank.texture}:${plank.matCode}:${plank.thick}`
  plank.renderUrl = (originData?.render_url as string) ?? ''
  plank.from = ((originData?.from as From) ?? From.BLUEN) as From
}

function getStoreDragItemNewStatus(currentDrag: StoreDragItem) {
  if ('new' in currentDrag) {
    return currentDrag.new ?? true
  }
  return true
}
